/**
 * Calculates the Levenshtein distance between two strings.
 * The Levenshtein distance is the minimum number of single-character edits
 * (insertions, deletions, or substitutions) required to change one string into another.
 *
 * @param source - The source string
 * @param target - The target string
 * @returns The Levenshtein distance between the two strings
 *
 * @example
 * levenshteinDistance("kitten", "sitting") // returns 3
 * levenshteinDistance("hello", "hello") // returns 0
 * levenshteinDistance("", "abc") // returns 3
 */
export function levenshteinDistance(source: string, target: string): number {
  // Handle edge cases
  if (source === target) return 0;
  if (source.length === 0) return target.length;
  if (target.length === 0) return source.length;

  // Use flat arrays for the two active rows (previous and current)
  let prev: number[] = Array.from({ length: source.length + 1 }, (_, j) => j);
  let curr: number[] = new Array(source.length + 1) as number[];

  for (let i = 1; i <= target.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= source.length; j++) {
      const prevRow = prev;
      const substitutionCost = prevRow[j - 1] ?? 0;
      const deletionCost = prevRow[j] ?? 0;
      const insertionCost = curr[j - 1] ?? 0;
      if (target.charAt(i - 1) === source.charAt(j - 1)) {
        curr[j] = substitutionCost;
      } else {
        curr[j] = Math.min(
          substitutionCost + 1, // substitution
          insertionCost + 1, // insertion
          deletionCost + 1 // deletion
        );
      }
    }
    [prev, curr] = [curr, prev];
  }

  return prev[source.length] ?? 0;
}
